/**
 * The stage's query state, exactly as pages/Data.tsx keeps it, so the cloud's scope logic is untouched:
 * `q`, the trail as `step` params (one JSON step each), `range` / `from` / `to` / `asOf` → the time params,
 * and `scopeParams()` that turns them into what every cloud call takes (q + and/andValue + from/to).
 * Pure helpers, copied from Data.tsx; no React.
 */

/**
 * A step of the trail: label, the value shown, the query term; vs = several values chosen, cs = their
 * counts, av = the ways the value is written in the systems. r = a record (an order) rather than an
 * attribute of the whole; x = a record opened by its identifier: it is searched by itself, the steps
 * before it are where it was found; c = its data model.
 */
export interface Step { l: string; v: string; t: string; vs?: string[]; cs?: number[]; av?: string[]; r?: 1; x?: 1; c?: string }

/** The lenses the map reveals in place (ui/DESIGN.md §3.5); `lens` in the URL. */
export type LensId = 'records' | 'runs' | 'changes' | 'values';
export const LENSES: LensId[] = ['records', 'runs', 'changes', 'values'];
export const isLens = (s: string | null): s is LensId => !!s && (LENSES as string[]).includes(s);

export type Range = '24h' | '7d' | '30d' | 'all';
export const RANGES: Range[] = ['24h', '7d', '30d', 'all'];
export const DEFAULT_RANGE: Range = '30d';
export const RANGE_MS: Record<Exclude<Range, 'all'>, number> = { '24h': 86_400_000, '7d': 7 * 86_400_000, '30d': 30 * 86_400_000 };

/** Narrows down to one record rather than to an attribute of what was searched. */
export const isRecord = (st: Step) => st.r === 1 || (!st.vs && !st.t.includes('=*'));

/** Quoted for the query syntax when it has spaces, quotes or parentheses. */
export const quoted = (v: string) => (/[\s"()]/.test(v) ? `"${v.replace(/"/g, '')}"` : v);

export const term = (path: string, value: string) => `${path}=${quoted(value)}`;

/** A value in any of the fields: (SalesId=SO-1 OR OrderNumber=SO-1). */
export const anyOf = (paths: string[], value: string) => {
  const ts = [...new Set(paths)].map((p) => term(p, value));
  return ts.length === 1 ? ts[0] : `(${ts.join(' OR ')})`;
};

/** The trail from the URL's `step` params; anything unreadable is dropped. */
export function parseSteps(raw: string[]): Step[] {
  return raw.map((x) => {
    try {
      return JSON.parse(x) as Step;
    } catch {
      return null;
    }
  }).filter((x): x is Step => !!x && typeof x.t === 'string' && typeof x.v === 'string' && typeof x.l === 'string');
}

/** The search and the steps as query parameters: a record opened by its identifier is searched by itself. */
export function scopeParams(q: string, steps: Step[], time: URLSearchParams): URLSearchParams {
  const p = new URLSearchParams(time);
  const opened = steps.map((st, i) => (st.x ? i : -1)).filter((i) => i >= 0).pop() ?? -1;
  p.set('q', opened >= 0 ? quoted(steps[opened].v) : q);
  for (const st of steps.slice(opened + 1)) {
    p.append('and', st.t);
    for (const v of st.av ?? st.vs ?? [st.v]) p.append('andValue', v);
  }
  return p;
}

/** The as-of moment from the URL, a 12–13 digit epoch in ms, or null. */
export function parseAsOf(raw: string | null): number | null {
  return raw && /^\d{12,13}$/.test(raw) ? Number(raw) : null;
}

/**
 * The time params of every call: an explicit from/to, else up to the as-of moment and the range before
 * it, else "now-30d"; `all` sends nothing.
 */
export function timeParams(range: string, from: string | null, to: string | null, asOf: number | null): URLSearchParams {
  const p = new URLSearchParams();
  if (from) {
    p.set('from', from);
    if (to) p.set('to', to);
  } else if (asOf) {
    p.set('to', String(asOf));
    const m = /^(\d+)([mhd])$/.exec(range);
    if (m) p.set('from', String(asOf - Number(m[1]) * { m: 60_000, h: 3_600_000, d: 86_400_000 }[m[2] as 'm' | 'h' | 'd']));
  } else if (range !== 'all') {
    p.set('from', `now-${range}`);
  }
  return p;
}

/** When the window starts, for the scrubber's track: now minus the range, or where the data begins for `all`. */
export function windowStart(range: string, from: string | null, asOf: number | null, now: number, first: number | null): number {
  const end = asOf ?? now;
  if (from && /^\d{12,13}$/.test(from)) return Number(from);
  const m = /^(\d+)([mhd])$/.exec(range);
  if (m) return end - Number(m[1]) * { m: 60_000, h: 3_600_000, d: 86_400_000 }[m[2] as 'm' | 'h' | 'd'];
  return first ?? end - RANGE_MS['30d'];
}

/** Another value of an attribute already on the trail replaces it; the same term twice is nothing new. */
export function withStep(steps: Step[], step: Step): Step[] {
  if (steps.some((st) => st.t === step.t)) return steps;
  const i = steps.findIndex((st) => st.l === step.l);
  return i >= 0 ? steps.map((st, n) => (n === i ? step : st)) : [...steps, step];
}

/** The values on the trail, as the lenses need them to hide what is already chosen. */
export const trailValues = (steps: Step[]) => steps.flatMap((st) => st.vs ?? [st.v]);
